<script lang="ts" setup>
import UserAPI from '~/api/user';
import type { ResponseError } from '~/api/utils/types';

defineOptions({ inheritAttrs: false });
defineProps<{
  withPassword?: boolean;
}>();
const emit = defineEmits<{
  (event: 'update'): void;
  (event: 'cancel'): void;
}>();
const attrs = useAttrs();

const { t } = useI18n();
const modalStore = useModalStore();
const userStore = useUserStore();

const name = ref('');
const lastName = ref('');
const email = ref('');
const password = ref('');
const isVisiblePhoneVerification = ref(false);

const phone = usePhoneInput();

const isValidPhone = computed(() => checkPhoneIsValid(phone.value));
const isPhoneConfirmed = computed(
  () =>
    userStore.user?.isPhoneConfirmed && phone.value === userStore.user.phone,
);
const isPhoneConfirmationNecessary = computed(
  () =>
    !!(
      userStore.user?.phone &&
      comparePhone(phone.value, userStore.user.phone) &&
      !userStore.user.isPhoneConfirmed
    ),
);

const updateUserRequest = reactive(
  useRequest(
    UserAPI.update,
    () => ({
      body: {
        name: name.value,
        lastName: lastName.value,
        phone: userStore.user?.phone === phone.value ? undefined : phone.value,
        email: email.value,
        password: password.value || undefined,
      },
    }),
    { validation: () => isValidPhone.value },
  ),
);

const confirmPhoneRequest = reactive(useRequest(UserAPI.confirmPhone));

const onSubmit = () =>
  updateUserRequest
    .execute()
    ?.then((data) => {
      userStore.updateUser(data.user);
      emit('update');
      modalStore.showSuccess({
        title: t('message.success'),
        action: t('message.return'),
      });
    })
    .catch((e: ResponseError) => {
      modalStore.showError({
        title: getFetchErrorMessage(e, t('message.editing_error')),
        action: t('message.return'),
      });
    });

const onConfirmPhone = () => {
  confirmPhoneRequest
    .execute()
    ?.then(() => {
      isVisiblePhoneVerification.value = true;
    })
    .catch(() => {
      modalStore.showError({
        title: t('message.confirmation_error'),
        action: t('message.return'),
      });
    });
};

const onConfirmPhoneCompleted = () => {
  userStore.updatePhoneConfirmed(true);
  emit('update');
};

watch(
  () => userStore.user,
  () => {
    if (userStore.user) {
      name.value = userStore.user.name || '';
      lastName.value = userStore.user.lastName || '';
      email.value = userStore.user.email || '';
      phone.value = userStore.user.phone;
    }
  },
  { immediate: true },
);
</script>

<template>
  <form @submit.prevent="onSubmit" v-bind="attrs" class="profileEditUserForm">
    <div class="profileEditUserForm__inputList">
      <BaseTextField
        v-model:value="name"
        autocomplete="given-name"
        :placeholder="t('form.first_name')"
        bordered
        class="profileEditUserForm__input"
      />
      <BaseTextField
        v-model:value="lastName"
        autocomplete="family-name"
        :placeholder="t('form.last_name')"
        bordered
        class="profileEditUserForm__input"
      />
      <BaseTextField
        v-model:value="email"
        autocomplete="email"
        :placeholder="t('form.email')"
        type="email"
        inputmode="email"
        name="email"
        bordered
        class="profileEditUserForm__input"
      />

      <div>
        <label class="profileEditUserForm__phoneLabel">
          <BaseTextField
            :placeholder="t('form.phone')"
            bordered
            class="profileEditUserForm__input"
            v-bind="phone.inputProps"
          />
          <Transition name="fade">
            <SvgoSimpleCheckSolid
              v-if="isPhoneConfirmed"
              class="profileEditUserForm__phoneIcon"
            />
          </Transition>
        </label>
        <BaseCollapse
          :visible="isPhoneConfirmationNecessary"
          content-class="profileEditUserForm__phoneVerification"
        >
          <BaseButton
            @click="onConfirmPhone"
            :loading="confirmPhoneRequest.isLoading"
            dark
            block
            type="button"
          >
            {{ t('form.confirm_phone') }}
          </BaseButton>
        </BaseCollapse>
      </div>

      <BaseTextField
        v-if="withPassword"
        v-model:value="password"
        autocomplete="new-password"
        :placeholder="t('form.password')"
        type="password"
        bordered
        class="profileEditUserForm__input"
      />
    </div>

    <div class="profileEditUserForm__buttons">
      <BaseButton
        :disabled="!updateUserRequest.isValid"
        :loading="updateUserRequest.isLoading"
        dark
        type="submit"
        class="profileEditUserForm__action"
      >
        {{ t('form.save') }}
      </BaseButton>
      <BaseButton
        @click="emit('cancel')"
        dark
        outlined
        class="profileEditUserForm__action"
        type="button"
      >
        {{ t('form.cancel') }}
      </BaseButton>
    </div>
  </form>

  <ProfilePhoneVerificationModal
    v-model:visible="isVisiblePhoneVerification"
    @completed="onConfirmPhoneCompleted"
    :phone="phone.value"
  />
</template>

<i18n>
ru:
  form:
    first_name: Имя
    last_name: Фамилия
    email: Email
    phone: Телефон
    confirm_phone: Подтвердить телефон
    password: Задайте или измените пароль
    save: Сохранить изменения
    cancel: Отменить
  message:
    success: Данные профиля успешно изменены
    editing_error: Произошла ошибка при изменении данных профиля
    confirmation_error: Произошла ошибка при подтверждении номера телефона
    return: Вернуться

en:
  form:
    first_name: First name
    last_name: Last name
    email: Email
    phone: Phone
    confirm_phone: Confirm phone
    password: Set or change password
    save: Save changes
    cancel: Cancel
  message:
    success: Profile data successfully changed
    editing_error: An error occurred while changing profile data
    confirmation_error: An error occurred while confirming the phone number
    return: Return
</i18n>

<style lang="scss">
.profileEditUserForm {
  $self: &;

  &__input {
    width: 100%;
  }

  &__phoneLabel {
    position: relative;
    display: block;

    #{$self}__input input {
      padding-right: 32px;
    }
  }

  &__phoneIcon {
    position: absolute;
    top: 8px;
    right: 7px;
    width: 24px;
    height: 24px;
    display: block;
  }

  &__phoneVerification {
    padding-top: 16px;
  }

  &__inputList {
    padding-bottom: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    @include mq('sm') {
      padding-bottom: 16px;
    }
  }

  &__buttons {
    display: flex;
    gap: 10px;

    @include mq('md') {
      flex-direction: column-reverse;
    }
  }

  & &__action {
    padding: 0;

    &:first-child {
      width: 62%;

      @include mq('md') {
        width: 100%;
      }
    }

    &:last-child {
      flex: 1;

      @include mq('md') {
        flex: initial;
      }
    }
  }
}
</style>
